import React, { useEffect, useState } from 'react';
import { HeartPulseIcon } from 'lucide-react';

const DatosMedicos = ({ formData, handleInputChange, setFormData, errors }) => {
    const [localErrors, setLocalErrors] = useState({});

    useEffect(() => {
        setLocalErrors({});
    }, []);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.peso) {
            newErrors.peso = 'El peso es requerido';
        } else if (formData.peso < 0 || formData.peso > 150) {
            newErrors.peso = 'El peso debe estar entre 0 y 150 kg';
        }
        
        if (!formData.estatura) {
            newErrors.estatura = 'La estatura es requerida';
        } else if (formData.estatura < 0 || formData.estatura > 250) {
            newErrors.estatura = 'La estatura debe estar entre 0 y 250 cm';
        }

        if (!formData.alergias?.trim()) newErrors.alergias = 'El campo alergias es requerido';
        if (!formData.enfermedades?.trim()) newErrors.enfermedades = 'Las enfermedades padecidas son requeridas';
        
        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNumericInputChange = (e) => {
        const { name, value } = e.target;
        
        // Validaciones específicas por campo
        switch(name) {
            case 'semanasGestacion':
                if (value === '' || (value >= 0 && value <= 45)) {
                    handleInputChange(e);
                }
                break;
            case 'peso':
                if (value === '' || (value >= 0 && value <= 150)) {
                    handleInputChange(e);
                }
                break;
            case 'estatura':
                if (value === '' || (value >= 0 && value <= 250)) {
                    handleInputChange(e);
                }
                break;
            default:
                handleInputChange(e);
        }
    };

    const toggleCheckboxField = (fieldName) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    };

    return (
        <div className="bg-white rounded-lg p-4 max-w-4xl mx-auto">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2 flex items-center">
                <HeartPulseIcon className="mr-2 text-blue-500 h-5 w-5" />
                Datos Médicos del Niño/Niña
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-4">
                    <div>
                        <label htmlFor="tipoParto" className="block text-sm font-medium text-gray-700 mb-1">
                            Tipo de Parto
                        </label>
                        <select
                            id="tipoParto"
                            name="tipoParto"
                            value={formData.tipoParto}
                            onChange={handleInputChange}
                            className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                        >
                            <option value="">Seleccione el tipo de parto</option>
                            <option value="Natural">Natural</option>
                            <option value="Cesárea">Cesárea</option>
                        </select>
                    </div>

                    <div>
                        <label htmlFor="semanasGestacion" className="block text-sm font-medium text-gray-700 mb-1">
                            Número de semanas de gestación 
                        </label>
                        <input
                            type="number"
                            id="semanasGestacion"
                            name="semanasGestacion"
                            value={formData.semanasGestacion || 0}
                            onChange={handleNumericInputChange}
                            min="0"
                            max="45"
                            step="1"
                            className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                        />
                    </div>

                    {formData.tipoParto === 'Cesárea' && (
                        <div>
                            <label htmlFor="causaCesarea" className="block text-sm font-medium text-gray-700 mb-1">
                                Indique las causas de la cesárea
                            </label>
                            <input
                                type="text"
                                id="causaCesarea"
                                name="causaCesarea"
                                value={formData.causaCesarea}
                                onChange={handleInputChange}
                                placeholder="Ej: Posición del bebé, emergencia médica"
                                className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                            />
                        </div>
                    )}


                    <div>
                        <label htmlFor="peso" className="block text-sm font-medium text-gray-700 mb-1">
                            Peso (kg) <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            id="peso"
                            name="peso"
                            value={formData.peso}
                            onChange={handleNumericInputChange}
                            min="0"
                            max="150"
                            step="0.01"
                            placeholder="Ej: 3.5"
                            className={`mt-1 block w-full h-8 rounded-md border ${errors?.peso ? 'border-red-500' : 'border-gray-300'} bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm`}
                        />

                        {errors?.peso && <p className="text-red-500 text-xs mt-1">{errors.peso}</p>}
                    </div>

                    <div>
                        <label htmlFor="estatura" className="block text-sm font-medium text-gray-700 mb-1">
                            Estatura (cm) <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            id="estatura"
                            name="estatura"
                            value={formData.estatura}
                            onChange={handleNumericInputChange}
                            min="0"
                            max="250"
                            placeholder="Ej: 50"
                            className={`mt-1 block w-full h-8 rounded-md border ${errors?.estatura ? 'border-red-500' : 'border-gray-300'} bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm`}
                        />

                        {errors?.estatura && <p className="text-red-500 text-xs mt-1">{errors.estatura}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Complicaciones durante el parto 
                        </label>
                        <div className="flex items-center space-x-4">
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="complicacionesParto"
                                    checked={formData.complicacionesParto}
                                    onChange={() => toggleCheckboxField('complicacionesParto')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">Sí</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="complicacionesParto"
                                    checked={!formData.complicacionesParto}
                                    onChange={() => toggleCheckboxField('complicacionesParto')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">No</span>
                            </label>
                        </div>
                        {formData.complicacionesParto && (
                            <input
                                type="text"
                                name="complicacionesPartoDetalle"
                                value={formData.complicacionesPartoDetalle}
                                onChange={handleInputChange}
                                className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                                placeholder="Ej: Falta de oxígeno, uso de fórceps"
                            />

                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Complicaciones durante el embarazo 
                        </label>
                        <div className="flex items-center space-x-4">
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="complicacionesEmbarazo"
                                    checked={formData.complicacionesEmbarazo}
                                    onChange={() => toggleCheckboxField('complicacionesEmbarazo')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">Sí</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="complicacionesEmbarazo"
                                    checked={!formData.complicacionesEmbarazo}
                                    onChange={() => toggleCheckboxField('complicacionesEmbarazo')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">No</span>
                            </label>
                        </div>
                        {formData.complicacionesEmbarazo && (
                            <input
                                type="text"
                                name="complicacionesEmbarazoDetalle"
                                value={formData.complicacionesEmbarazoDetalle}
                                onChange={handleInputChange}
                                className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                                placeholder="Ej: Diabetes gestacional, presión alta"
                            />

                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Requirió cuidados extras (termo-cuna, transfusiones, etc.) 
                        </label>
                        <div className="flex items-center space-x-4">
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="cuidadosExtras"
                                    checked={formData.cuidadosExtras}
                                    onChange={() => toggleCheckboxField('cuidadosExtras')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">Sí</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="cuidadosExtras"
                                    checked={!formData.cuidadosExtras}
                                    onChange={() => toggleCheckboxField('cuidadosExtras')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">No</span>
                            </label>
                        </div>
                        {formData.cuidadosExtras && (
                            <input
                                type="text"
                                name="cuidadosExtrasDetalle"
                                value={formData.cuidadosExtrasDetalle}
                                onChange={handleInputChange}
                                className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                                placeholder="Ej: Incubadora por 2 semanas, fototerapia"

                            />
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Control Neurológico
                        </label>
                        <div className="flex items-center space-x-4">
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="controlNeurologico"
                                    checked={formData.controlNeurologico}
                                    onChange={() => toggleCheckboxField('controlNeurologico')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">Sí</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="controlNeurologico"
                                    checked={!formData.controlNeurologico}
                                    onChange={() => toggleCheckboxField('controlNeurologico')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">No</span>
                            </label>
                        </div>
                        {formData.controlNeurologico && (
                            <input
                                type="text"
                                name="controlNeurologicoDetalle"
                                value={formData.controlNeurologicoDetalle}
                                onChange={handleInputChange}
                                className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                                placeholder="Ej: Control por epilepsia, TDAH"
                            />

                        )}
                    </div>
                </div>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="enfermedades" className="block text-sm font-medium text-gray-700 mb-1">
                            Especifique las enfermedades de las que ha padecido su hijo/a  <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="enfermedades"
                            name="enfermedades"
                            value={formData.enfermedades}
                            onChange={handleInputChange}
                            placeholder="Ej: Varicela, sarampión, bronquitis"
                            className={`mt-1 block w-full h-8 rounded-md border ${errors?.enfermedades ? 'border-red-500' : 'border-gray-300'} bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm`}

                        />
                        {errors?.enfermedades && <p className="text-red-500 text-xs mt-1">{errors.enfermedades}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Problemas de la Visión 
                        </label>
                        <div className="flex items-center space-x-4">
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="problemasVision"
                                    checked={formData.problemasVision}
                                    onChange={() => toggleCheckboxField('problemasVision')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">Sí</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="problemasVision"
                                    checked={!formData.problemasVision}
                                    onChange={() => toggleCheckboxField('problemasVision')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">No</span>
                            </label>
                        </div>
                        {formData.problemasVision && (
                            <input
                                type="text"
                                name="problemasVisionDetalle"
                                value={formData.problemasVisionDetalle}
                                onChange={handleInputChange}
                                className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                                placeholder="Ej: Miopía, astigmatismo"
                            />

                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Problemas de Audición 
                        </label>
                        <div className="flex items-center space-x-4">
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="problemasAudicion"
                                    checked={formData.problemasAudicion}
                                    onChange={() => toggleCheckboxField('problemasAudicion')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">Sí</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="problemasAudicion"
                                    checked={!formData.problemasAudicion}
                                    onChange={() => toggleCheckboxField('problemasAudicion')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">No</span>
                            </label>
                        </div>
                        {formData.problemasAudicion && (
                            <input
                                type="text"
                                name="problemasAudicionDetalle"
                                value={formData.problemasAudicionDetalle}
                                onChange={handleInputChange}
                                className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                                placeholder="Ej: Pérdida auditiva parcial, infecciones frecuentes"

                            />
                        )}
                    </div>

                    <div>
                        <label htmlFor="ingresosHospitalarios" className="block text-sm font-medium text-gray-700 mb-1">
                            ¿Ha tenido ingresos hospitalarios? 
                        </label>
                        <input
                            type="text"
                            id="ingresosHospitalarios"
                            name="ingresosHospitalarios"
                            value={formData.ingresosHospitalarios}
                            onChange={handleInputChange}
                            placeholder="Ej: Hospitalización por neumonía en 2022"
                            className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                        />
                    </div>


                    <div>
                        <label htmlFor="alergias" className="block text-sm font-medium text-gray-700 mb-1">
                            ¿Tiene alguna alergia? <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="alergias"
                            name="alergias"
                            value={formData.alergias}
                            onChange={handleInputChange}
                            placeholder="Ej: Polen, maní, lactosa"
                            className={`mt-1 block w-full h-8 rounded-md border ${errors?.alergias ? 'border-red-500' : 'border-gray-300'} bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm`}

                        />
                        {errors?.alergias && <p className="text-red-500 text-xs mt-1">{errors.alergias}</p>}
                    </div>

                    <div>
                        <label htmlFor="accidentes" className="block text-sm font-medium text-gray-700 mb-1">
                            Accidentes importantes 
                        </label>
                        <input
                            type="text"
                            id="accidentes"
                            name="accidentes"
                            value={formData.accidentes}
                            onChange={handleInputChange}
                            placeholder="Ej: Fractura de brazo en 2021"
                            className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                        />
                    </div>


                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            ¿Ha tenido operaciones quirúrgicas? 
                        </label>
                        <div className="flex items-center space-x-4">
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="operacionesQuirurgicas"
                                    checked={formData.operacionesQuirurgicas}
                                    onChange={() => toggleCheckboxField('operacionesQuirurgicas')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">Sí</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="operacionesQuirurgicas"
                                    checked={!formData.operacionesQuirurgicas}
                                    onChange={() => toggleCheckboxField('operacionesQuirurgicas')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">No</span>
                            </label>
                        </div>
                        {formData.operacionesQuirurgicas && (
                            <input
                                type="text"
                                name="operacionesQuirurgicasDetalle"
                                value={formData.operacionesQuirurgicasDetalle}
                                onChange={handleInputChange}
                                className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                                placeholder="Ej: Amigdalectomía en 2020"

                            />
                        )}
                    </div>

                    <div>
                        <label htmlFor="enfermedadesCronicas" className="block text-sm font-medium text-gray-700 mb-1">
                            Enfermedades crónicas
                        </label>
                        <input
                            type="text"
                            id="enfermedadesCronicas"
                            name="enfermedadesCronicas"
                            value={formData.enfermedadesCronicas}
                            onChange={handleInputChange}
                            placeholder="Ej: Asma, diabetes tipo 1"
                            className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                        />
                    </div>


                    <div>
                        <label htmlFor="antecedentesFamiliares" className="block text-sm font-medium text-gray-700 mb-1">
                            Antecedentes familiares 
                        </label>
                        <input
                            type="text"
                            id="antecedentesFamiliares"
                            name="antecedentesFamiliares"
                            value={formData.antecedentesFamiliares}
                            onChange={handleInputChange}
                            placeholder="Ej: Diabetes, hipertensión"
                            className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                        />
                    </div>


                   

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Discapacidad 
                        </label>
                        <div className="flex items-center space-x-4">
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="discapacidad"
                                    checked={formData.discapacidad}
                                    onChange={() => toggleCheckboxField('discapacidad')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">Sí</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="discapacidad"
                                    checked={!formData.discapacidad}
                                    onChange={() => toggleCheckboxField('discapacidad')}
                                    className="form-radio text-blue-600"
                                /> <span className="ml-2 text-sm text-gray-700">No</span>
                            </label>
                        </div>
                        {formData.discapacidad && (
                            <input
                                type="text"
                                name="discapacidadDetalle"
                                value={formData.discapacidadDetalle}
                                onChange={handleInputChange}
                                className="mt-1 block w-full h-8 rounded-md border border-gray-300 bg-white py-1 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                                placeholder="Ej: Discapacidad motriz, auditiva"
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DatosMedicos;
